import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { isMobile } from 'react-device-detect'
import { Helmet } from 'react-helmet'
import { CartItem } from '@open-tender/types'
import { makeUpsellItemsForCart } from '@open-tender/utils'
import {
  selectBrand,
  selectCart,
  selectCartIds,
  selectContentSection,
  selectCustomer,
  selectMenu,
  selectMenuSlug,
  selectQuote,
  useAppSelector,
} from '@open-tender/cloud'
import { Back, Skip } from 'components/buttons'
import {
  Button,
  ButtonLink,
  Content,
  Header,
  HeaderLogo,
  Main,
  UpsellItems,
} from 'components'
import {
  UpsellFooter,
  UpsellHeader,
  UpsellSubtitle,
  UpsellTitle,
  UpsellView,
} from './Upsell.styled'
import { sendUpsillsMenuView } from 'app/analytics'
import { makeUpsellsAnalyticsItems } from 'utils/helpers'

const Upsell = () => {
  const navigate = useNavigate()
  const [itemAdded, setItemAdded] = useState(false)
  const { title: siteTitle } = useAppSelector(selectBrand) || {}
  const { auth } = useAppSelector(selectCustomer)
  const quote = useAppSelector(selectQuote)
  const isCateringQuote = quote.order?.order_type === 'CATERING'
  const menuSlug = useAppSelector(selectMenuSlug)
  const cartIds = useAppSelector(selectCartIds)
  const cart = useAppSelector(selectCart)
  const { categories, soldOut } = useAppSelector(selectMenu)
  const upsellItems = useMemo(
    () => makeUpsellItemsForCart(categories, cartIds, soldOut) as CartItem[],
    [categories, cartIds, soldOut]
  )
  const items = useMemo(() => {
    return cart ? makeUpsellsAnalyticsItems(cart, upsellItems, 'CHECKOUT') : []
  }, [cart, upsellItems])

  const upsells = useAppSelector(selectContentSection('upsells'))
  const { show, title, subtitle, decline, proceed } = upsells?.checkout || {}
  const hasItems = upsellItems.length > 0
  const path = auth || isCateringQuote ? '/checkout' : '/checkout/guest'
  const skip = !show || !hasItems

  const checkout = () => navigate(path)

  useEffect(() => {
    if (!skip) {
      sendUpsillsMenuView({ position: 'CHECKOUT', items })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip])

  useEffect(() => {
    if (skip) navigate(path)
  }, [skip, path, navigate])

  if (skip) return null

  return (
    <>
      <Helmet>
        <title>Add-ons | {siteTitle}</title>
      </Helmet>
      <Content>
        <Header
          title={<HeaderLogo />}
          left={
            <Back
              label="Back to Menu"
              path={menuSlug}
              text={isMobile ? null : 'Back to Menu'}
            />
          }
          right={<Skip path={path} text="Skip" />}
        />
        <Main>
          <UpsellView>
            <UpsellHeader>
              <UpsellTitle>{title}</UpsellTitle>
              {subtitle && <UpsellSubtitle>{subtitle}</UpsellSubtitle>}
            </UpsellHeader>
            <UpsellItems
              upsellItems={items as CartItem[]}
              isSmall={isMobile}
              addCallback={() => setItemAdded(true)}
            />
            <UpsellFooter>
              {itemAdded ? (
                <Button onClick={checkout}>{proceed}</Button>
              ) : (
                <ButtonLink onClick={checkout}>
                  {decline || 'No thanks'}
                </ButtonLink>
              )}
            </UpsellFooter>
          </UpsellView>
        </Main>
      </Content>
    </>
  )
}

export default Upsell
